/* eslint-disable max-lines */
import { Box, Divider, Stack, type Theme } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useFetchSubstage } from "@src/appV2/redesign/ClipboardScore/hooks/useFetchSubstage";
import { Button } from "@src/appV2/redesign/components/Button";
import { CbhIcon, Pill, Text, TextSkeleton, Title } from "@src/appV2/redesign/system/ui-components";
import {
  type SkillsAssessment,
  type SkillsAssessmentStatus,
} from "@src/appV2/SkillsAssessment/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { formatDuration } from "date-fns";
import pluralize from "pluralize";

import { LimitedSubStageBannerUI } from "./LimitedStageBannerUI";

interface Props {
  onContinue: () => void;
  orientation: SkillsAssessment;
  orientationStatus: SkillsAssessmentStatus;
}

function getOnTimeWorkerContent(params: {
  orientation: SkillsAssessment;
  subStageValues: ReturnType<typeof useFetchSubstage>;
}): Array<{
  label: React.ReactNode;
  pill: React.ReactNode;
}> {
  const { orientation, subStageValues } = params;

  const scoreRewarded =
    orientation.attributes.experimentDetails?.experimentMetadata.actualScoreRewarded;

  const initialLeadTimeInHours =
    orientation.attributes.experimentDetails?.experimentMetadata.initialValues.leadTimeInHours;
  const currentLeadTimeInHours = subStageValues.data?.accessMetadata?.leadTimeThresholdInHrs;

  const currentBasketSize = subStageValues.data?.accessMetadata?.basketSize;
  const initialBasketSize =
    orientation.attributes.experimentDetails?.experimentMetadata.initialValues.basketSize;

  const formattedCurrentLeadTime = currentLeadTimeInHours
    ? formatDuration(
        {
          days: Math.floor(currentLeadTimeInHours / 24),
          hours: currentLeadTimeInHours % 24,
        },
        {
          format: currentLeadTimeInHours >= 24 ? ["days", "hours"] : ["hours"],
        }
      )
    : "";

  const pillTextStyle = (theme: Theme) => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
  });

  const pillStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.intent?.success.background,
  });

  return [
    ...(scoreRewarded
      ? [
          {
            label: (
              <Text variant="body2" color="text.secondary">
                You gained Clipboard Score points
              </Text>
            ),
            pill: (
              <Pill
                sx={pillStyle}
                label={
                  <Text sx={pillTextStyle} variant="body2">
                    {`+${scoreRewarded} pts`}
                  </Text>
                }
                size="small"
                variant="filled"
              />
            ),
          },
        ]
      : []),
    ...(formattedCurrentLeadTime
      ? [
          {
            // In case lead time is same as initial lead time, we want to show user only current lead time
            label: (
              <Text variant="body2" color="text.secondary">
                {currentLeadTimeInHours &&
                initialLeadTimeInHours &&
                currentLeadTimeInHours > initialLeadTimeInHours
                  ? `Book further than ${initialLeadTimeInHours} hours ahead`
                  : `Book ${formattedCurrentLeadTime} ahead`}
              </Text>
            ),

            pill: (
              <Pill
                sx={pillStyle}
                label={
                  <Text sx={pillTextStyle} variant="body2">
                    {formattedCurrentLeadTime}
                  </Text>
                }
                size="small"
                variant="filled"
              />
            ),
          },
        ]
      : []),
    ...(currentBasketSize
      ? [
          {
            label: (
              <Text variant="body2" color="text.secondary">
                {currentBasketSize && initialBasketSize && currentBasketSize > initialBasketSize
                  ? `Book more than ${initialBasketSize} shifts`
                  : `Bookings at a time`}
              </Text>
            ),
            pill: (
              <Pill
                sx={pillStyle}
                label={
                  <Text sx={pillTextStyle} variant="body2">
                    {`${currentBasketSize} ${pluralize("shift", currentBasketSize)}`}
                  </Text>
                }
                size="small"
                variant="filled"
              />
            ),
          },
        ]
      : []),
  ];
}

function getLateWorkerContent(params: {
  orientation: SkillsAssessment;
  subStageValues: ReturnType<typeof useFetchSubstage>;
}): Array<{
  label: React.ReactNode;
  pill: React.ReactNode;
}> {
  const { orientation, subStageValues } = params;

  const currentLeadTimeInHours = subStageValues.data?.accessMetadata?.leadTimeThresholdInHrs;
  const onTimeLeadTimeInHours =
    orientation.attributes.experimentDetails?.experimentMetadata?.onTimeReward
      ?.newShiftBookingLeadTimeInHours;

  const couldHaveGainedMoreLeadTime = Boolean(
    onTimeLeadTimeInHours &&
    currentLeadTimeInHours &&
    onTimeLeadTimeInHours > currentLeadTimeInHours
  );

  let leadTimeContent:
    | {
        label: React.ReactNode;
        pill: React.ReactNode;
      }
    | undefined;
  if (couldHaveGainedMoreLeadTime && currentLeadTimeInHours && onTimeLeadTimeInHours) {
    leadTimeContent = {
      label: (
        <Text sx={{ textDecoration: "line-through" }} variant="body2" color="text.secondary">
          {`Book further than ${currentLeadTimeInHours} hours ahead`}
        </Text>
      ),
      pill: (
        <Pill
          sx={(theme) => ({
            backgroundColor: theme.palette.intent?.disabled.background,
          })}
          label={
            <Text
              sx={(theme) => ({
                color: theme.palette.intent?.disabled.text,
                textDecoration: "line-through",
                fontWeight: 700,
              })}
              variant="body2"
            >
              {formatDuration(
                {
                  days: Math.floor(onTimeLeadTimeInHours / 24),
                  hours: onTimeLeadTimeInHours % 24,
                },
                {
                  format: onTimeLeadTimeInHours >= 24 ? ["days", "hours"] : ["hours"],
                }
              )}
            </Text>
          }
          size="small"
          variant="filled"
        />
      ),
    };
  } else if (currentLeadTimeInHours) {
    const formattedCurrentLeadTime = formatDuration(
      {
        days: Math.floor(currentLeadTimeInHours / 24),
        hours: currentLeadTimeInHours % 24,
      },
      {
        format: currentLeadTimeInHours >= 24 ? ["days", "hours"] : ["hours"],
      }
    );
    leadTimeContent = {
      label: (
        <Text variant="body2" color="text.secondary">
          {`Book further than ${formattedCurrentLeadTime} ahead`}
        </Text>
      ),
      pill: (
        <Pill
          sx={(theme) => ({
            backgroundColor: theme.palette.intent?.success.background,
          })}
          label={
            <Text
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 700,
              })}
              variant="body2"
            >
              {formattedCurrentLeadTime}
            </Text>
          }
          size="small"
          variant="filled"
        />
      ),
    };
  }

  let basketSizeContent:
    | {
        label: React.ReactNode;
        pill: React.ReactNode;
      }
    | undefined;
  const currentBasketSize = subStageValues.data?.accessMetadata?.basketSize;

  const onTimeBasketSize =
    orientation.attributes.experimentDetails?.experimentMetadata?.onTimeReward.newShiftBasketSize;

  const couldHaveGainedMoreBasketSize = Boolean(
    onTimeBasketSize && currentBasketSize && onTimeBasketSize > currentBasketSize
  );

  if (couldHaveGainedMoreBasketSize && currentBasketSize && onTimeBasketSize) {
    basketSizeContent = {
      label: (
        <Text sx={{ textDecoration: "line-through" }} variant="body2" color="text.secondary">
          {`Book more than ${currentBasketSize} shifts`}
        </Text>
      ),
      pill: (
        <Pill
          sx={(theme) => ({
            backgroundColor: theme.palette.intent?.disabled.background,
          })}
          label={
            <Text
              sx={(theme) => ({
                color: theme.palette.intent?.disabled.text,
                textDecoration: "line-through",
                fontWeight: 700,
              })}
              variant="body2"
            >
              {`${onTimeBasketSize} ${pluralize("shift", onTimeBasketSize)}`}
            </Text>
          }
          size="small"
          variant="filled"
        />
      ),
    };
  } else if (currentBasketSize) {
    basketSizeContent = {
      label: (
        <Text variant="body2" color="text.secondary">
          Bookings at a time
        </Text>
      ),
      pill: (
        <Pill
          sx={(theme) => ({
            backgroundColor: theme.palette.intent?.success.background,
          })}
          label={
            <Text
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 700,
              })}
              variant="body2"
            >
              {`${currentBasketSize} ${pluralize("shift", currentBasketSize)}`}
            </Text>
          }
          size="small"
          variant="filled"
        />
      ),
    };
  }

  const onTimeScoreReward =
    orientation.attributes.experimentDetails?.experimentMetadata?.onTimeReward
      ?.clipboardScoreAdjustment;

  return [
    ...(onTimeScoreReward
      ? [
          {
            label: (
              <Text sx={{ textDecoration: "line-through" }} variant="body2" color="text.secondary">
                Gain Clipboard Score points
              </Text>
            ),
            pill: (
              <Pill
                sx={(theme) => ({
                  backgroundColor: theme.palette.intent?.disabled.background,
                })}
                label={
                  <Text
                    sx={(theme) => ({
                      color: theme.palette.intent?.disabled.text,
                      textDecoration: "line-through",
                      fontWeight: 700,
                    })}
                    variant="body2"
                  >
                    {`+${onTimeScoreReward} pts`}
                  </Text>
                }
                size="small"
                variant="filled"
              />
            ),
          },
        ]
      : []),
    ...(leadTimeContent ? [leadTimeContent] : []),
    ...(basketSizeContent ? [basketSizeContent] : []),
  ];
}

export function TimelinessRewardScreen(props: Props) {
  const { onContinue, orientation, orientationStatus } = props;
  const worker = useDefinedWorker();

  const isOnTime = orientationStatus.attributes.isStartedOnTime;

  const subStageValues = useFetchSubstage();

  const { data: { clipboardScore } = {}, status: scoreStatus } = useGetClipboardScoreProfile(
    worker.userId
  );

  if (scoreStatus === "loading" || subStageValues.status === "loading") {
    return (
      <Stack height="100%" gap={6}>
        <TextSkeleton variant="h1" />
        <TextSkeleton variant="h2" />
        <br />
        <br />

        <TextSkeleton variant="h3" />
        <TextSkeleton variant="h3" />
        <TextSkeleton variant="h3" />
        <TextSkeleton variant="h3" />
        <TextSkeleton variant="h3" />
      </Stack>
    );
  }

  const content = isOnTime
    ? getOnTimeWorkerContent({ orientation, subStageValues })
    : getLateWorkerContent({ orientation, subStageValues });

  return (
    <Stack flex={1} pt={6} justifyContent="space-between">
      <Stack gap={4}>
        <Stack spacing={4}>
          <Title bold variant="h3" component="h3">
            {isOnTime
              ? "One step closer to unlimited access!"
              : "Late to your Demo Shift - no bonus! "}
          </Title>

          <Text light variant="body1">
            {isOnTime
              ? "You showed up on time to your Demo Shift and are now much closer to unlimited shift access."
              : "You did not show up at the scheduled time but you can still work to gain unlimited shift access."}
          </Text>
        </Stack>

        <Stack direction="row" alignItems="center" gap={4}>
          <Box>
            <CbhIcon type="bolt-filled" />
          </Box>
          <Divider sx={{ flexGrow: 1 }} />
        </Stack>

        <Stack spacing={4}>
          {clipboardScore &&
            subStageValues.data?.name === "LIMITED" &&
            subStageValues.data?.accessMetadata?.scoreThreshold && (
              <LimitedSubStageBannerUI
                sx={(theme) => ({
                  background: isOnTime
                    ? theme.palette.onboarding?.inProgress.gradient
                    : // TODO @mohit: Add gradient for late start in ui-components later within 1 day of raising this PR
                      "linear-gradient(111deg, #FBF2E8 7.61%, #EBEBEB 96.83%)",
                })}
                score={clipboardScore}
                thresholdScore={subStageValues.data.accessMetadata.scoreThreshold}
              />
            )}

          {content.map((item, index) => (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={`key-${index}`} // Content is static, so we can use index as key
              direction="row"
              alignItems="center"
              gap={1}
              justifyContent="space-between"
            >
              {item.label}
              {item.pill}
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Button variant="contained" onClick={onContinue}>
        Continue
      </Button>
    </Stack>
  );
}
/* eslint-enable max-lines */
